import type { EventEnvelope } from '../types/envelope.js';
import { generateId, getOrCreateSessionId } from './session.js';

/** Raw track payload from app (before enrichment). */
export interface TrackPayload {
  name: string;
  properties?: Record<string, unknown>;
  timestamp?: string;
}

export interface EnricherState {
  appId: string;
  environment: string;
  sdkVersion: string;
  sessionId?: string;
  userId?: string;
  /** Provided at init or generated per session. */
  resolvedSessionId?: string;
}

const ENVELOPE_VERSION = 1;

/**
 * Creates enricher state from init config. Call once at init.
 */
export function createEnricherState(
  appId: string,
  environment: string,
  sdkVersion: string,
  sessionId?: string
): EnricherState {
  const resolvedSessionId = getOrCreateSessionId(appId, sessionId);
  return {
    appId,
    environment,
    sdkVersion,
    sessionId,
    resolvedSessionId,
  };
}

/**
 * Enrich a raw track payload into a full EventEnvelope.
 */
export function enrich(
  payload: TrackPayload,
  state: EnricherState
): EventEnvelope {
  const timestamp = payload.timestamp ?? new Date().toISOString();
  const envelope: EventEnvelope = {
    name: payload.name,
    timestamp,
    eventId: generateId(),
    appId: state.appId,
    environment: state.environment,
    sdkVersion: state.sdkVersion,
    version: ENVELOPE_VERSION,
  };
  if (payload.properties != null) envelope.properties = payload.properties;
  if (state.resolvedSessionId) envelope.sessionId = state.resolvedSessionId;
  if (state.userId) envelope.userId = state.userId;
  return envelope;
}

/**
 * Set userId (and optional traits) on state after identify. Cleared on reset.
 */
export function setUserId(state: EnricherState, userId: string): void {
  state.userId = userId;
}

/**
 * Clear userId from state (e.g. on reset).
 */
export function clearUserId(state: EnricherState): void {
  state.userId = undefined;
}
